import React from "react";
import { Modal } from "../audiences/ui";
import { cleanTitle } from "../reports/panel";
import { CATALOG, SIZE_LABEL, MAX_CARDS, entryOf, available, isPinned, listReportPanels } from "./layout";

/*
 * My Dashboard edit mode (PlunkDocs specs/MY_DASHBOARD_2026-10-22.md, mockups A, B, D, F): the bar under the header,
 * the tools on each card, the Add card sheet, the workspace-default confirm and the undo toast.
 * Phones get Move up / Move down and Remove; sizes are desktop only (CSS hides them under 720px).
 */
const btn = "k-button k-button-md k-rounded-md k-button-solid ";
export function EditBar({
  count,
  canSetDefault,
  hasDefault,
  canReset,
  resetLabel,
  busy,
  onAdd,
  onReset,
  onSaveDefault,
  onClearDefault,
  onCancel,
  onDone
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: "dash-editbar",
    role: "region",
    "aria-label": "Customize Dashboard"
  }, /*#__PURE__*/React.createElement("span", {
    className: "dash-editbar-tx"
  }, /*#__PURE__*/React.createElement("b", null, "Customizing your Dashboard."), " ", /*#__PURE__*/React.createElement("span", {
    className: "dash-desk"
  }, "Drag cards, pick sizes, or add more."), /*#__PURE__*/React.createElement("span", {
    className: "dash-phone"
  }, "Move, remove or add cards.")), /*#__PURE__*/React.createElement("span", {
    className: "dash-editbar-acts"
  }, canReset && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "nc-link",
    disabled: busy,
    onClick: onReset
  }, resetLabel), canSetDefault && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "nc-link",
    disabled: busy || !count,
    onClick: onSaveDefault
  }, "Save as the workspace default"), canSetDefault && hasDefault && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "nc-link",
    disabled: busy,
    onClick: onClearDefault
  }, "Remove the workspace default"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: btn + "k-button-solid-base",
    disabled: busy || count >= MAX_CARDS,
    title: count >= MAX_CARDS ? `A Dashboard holds up to ${MAX_CARDS} cards` : null,
    onClick: onAdd
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons"
  }, "add"), "Add card"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: btn + "k-button-solid-base",
    disabled: busy,
    onClick: onCancel
  }, "Cancel"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: btn + "k-button-solid-primary",
    disabled: busy,
    onClick: onDone
  }, busy ? "Saving…" : "Done")));
}
const REPORT_SIZES = [4, 6, 8, 12];
/** The strip on top of a card in edit mode: drag handle, name, sizes, move, remove. */
export function CardTools({
  card,
  label,
  index,
  total,
  size,
  onSize,
  onMove,
  onRemove
}) {
  const e = card.source === "report" ? {
    label: label || "Report chart",
    sizes: REPORT_SIZES
  } : entryOf(card.key) || {
    label: card.key,
    sizes: [12]
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "dash-tools"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons dash-grip",
    "aria-hidden": "true"
  }, "drag_indicator"), /*#__PURE__*/React.createElement("b", {
    className: "dash-tools-name"
  }, e.label), e.sizes.length > 1 && /*#__PURE__*/React.createElement("span", {
    className: "seg dash-sizes",
    role: "group",
    "aria-label": `${e.label} size`
  }, e.sizes.map(s => /*#__PURE__*/React.createElement("button", {
    key: s,
    type: "button",
    className: size === s ? "on" : "",
    "aria-pressed": size === s,
    onClick: () => onSize(s)
  }, SIZE_LABEL[s]))), /*#__PURE__*/React.createElement("span", {
    className: "dash-tools-acts"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "dash-ic",
    disabled: index === 0,
    "aria-label": `Move ${e.label} up`,
    title: "Move up",
    onClick: () => onMove(-1)
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons"
  }, "arrow_upward")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "dash-ic",
    disabled: index === total - 1,
    "aria-label": `Move ${e.label} down`,
    title: "Move down",
    onClick: () => onMove(1)
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons"
  }, "arrow_downward")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "dash-ic",
    "aria-label": `Remove ${e.label}`,
    title: "Remove",
    onClick: onRemove
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons"
  }, "close"))));
}
const KIND_ICON = {
  kpi: "tag",
  line: "show_chart",
  bar: "bar_chart",
  table: "table_rows"
};
const KIND_NAME = {
  kpi: "KPI",
  line: "Line",
  bar: "Bar",
  table: "Table"
};
// Reports saved without an email claim carry the Cognito id as their creator: "a teammate", as the Reports list says.
const nameOf = email => {
  const e = String(email || "");
  if (!e.includes("@")) return "a teammate";
  const l = e.split("@")[0].split(/[._+-]/).filter(Boolean);
  return l.length ? l.map(x => x[0].toUpperCase() + x.slice(1)).join(" ") : "a teammate";
};

/** Add card (mockup B): Plunk cards, any panel of a report the person can see, or a new chart (the builder's one-chart mode). */
export function AddCardSheet({
  cards,
  ctx,
  onAdd,
  onAddReport,
  onNewChart,
  onClose
}) {
  const [tab, setTab] = React.useState("plunk");
  const [reports, setReports] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    if (tab !== "reports" || reports) return;
    listReportPanels().then(setReports).catch(() => {
      setReports([]);
      setFailed(true);
    });
  }, [tab]);
  const on = new Set(cards.filter(c => c.source === "plunk").map(c => c.key));
  const full = cards.length >= MAX_CARDS;
  const list = CATALOG.filter(e => available({
    source: "plunk",
    key: e.key
  }, ctx));
  const addBtn = onClick => /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: btn + "k-button-solid-base",
    disabled: full,
    onClick: onClick
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons"
  }, "add"), "Add");
  return /*#__PURE__*/React.createElement(Modal, {
    title: "Add a card",
    onClose: onClose,
    width: 600,
    footer: /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: btn + "k-button-solid-base",
      onClick: onClose
    }, "Close"),
    note: full ? `A Dashboard holds up to ${MAX_CARDS} cards.` : null
  }, /*#__PURE__*/React.createElement("span", {
    className: "seg dash-lib-tabs",
    role: "tablist"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    role: "tab",
    "aria-selected": tab === "plunk",
    className: tab === "plunk" ? "on" : "",
    onClick: () => setTab("plunk")
  }, "Plunk cards"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    role: "tab",
    "aria-selected": tab === "reports",
    className: tab === "reports" ? "on" : "",
    onClick: () => setTab("reports")
  }, "From reports"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    role: "tab",
    "aria-selected": tab === "new",
    className: tab === "new" ? "on" : "",
    onClick: () => setTab("new")
  }, "New chart")), tab === "new" ? /*#__PURE__*/React.createElement("div", {
    className: "dash-lib dash-new"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons dash-new-ic",
    "aria-hidden": "true"
  }, "add_chart"), /*#__PURE__*/React.createElement("b", null, "Make a chart for your Dashboard"), /*#__PURE__*/React.createElement("p", null, "Pick what to measure and how to show it: numbers, a line, bars or a table, with live data as you go. It's saved in your private \"Dashboard charts\" report, so you can edit, share or email it from Reports later."), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: btn + "k-button-solid-primary",
    disabled: full,
    onClick: onNewChart
  }, "Open the chart builder"), /*#__PURE__*/React.createElement("small", null, "Changes you've made to your Dashboard are saved first.")) : tab === "plunk" ? /*#__PURE__*/React.createElement("div", {
    className: "dash-lib"
  }, list.map(e => /*#__PURE__*/React.createElement("div", {
    key: e.key,
    className: "dash-lib-row"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons dash-lib-ic",
    "aria-hidden": "true"
  }, e.icon), /*#__PURE__*/React.createElement("span", {
    className: "dash-lib-tx"
  }, /*#__PURE__*/React.createElement("b", null, e.label), /*#__PURE__*/React.createElement("small", null, e.desc)), on.has(e.key) ? /*#__PURE__*/React.createElement("span", {
    className: "dash-lib-on"
  }, "On Dashboard") : addBtn(() => onAdd(e.key))))) : /*#__PURE__*/React.createElement("div", {
    className: "dash-lib"
  }, reports === null && /*#__PURE__*/React.createElement("p", {
    className: "dash-lib-note"
  }, "Loading your reports\u2026"), failed && /*#__PURE__*/React.createElement("p", {
    className: "dash-lib-note"
  }, "Reports could not be loaded. Try again in a moment."), reports && !failed && reports.length === 0 && /*#__PURE__*/React.createElement("p", {
    className: "dash-lib-note"
  }, "No reports yet. Charts from reports you make or that are shared with you show up here."), (reports || []).map(r => /*#__PURE__*/React.createElement("div", {
    key: r.id,
    className: "dash-lib-group"
  }, /*#__PURE__*/React.createElement("div", {
    className: "dash-lib-head"
  }, /*#__PURE__*/React.createElement("b", null, r.name), /*#__PURE__*/React.createElement("small", null, r.mine ? r.shared ? "yours" : "yours · just you" : r.starter ? "starter" : `shared by ${nameOf(r.createdBy)}`)), (r.panels || []).map(p => /*#__PURE__*/React.createElement("div", {
    key: p.id,
    className: "dash-lib-row"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons dash-lib-ic",
    "aria-hidden": "true"
  }, KIND_ICON[p.kind] || "insert_chart"), /*#__PURE__*/React.createElement("span", {
    className: "dash-lib-tx"
  }, /*#__PURE__*/React.createElement("b", null, cleanTitle(p.title) || "Untitled chart"), /*#__PURE__*/React.createElement("small", null, KIND_NAME[p.kind] || p.kind, p.series ? ` · ${p.series} series` : "")), isPinned(cards, r.id, p.id) ? /*#__PURE__*/React.createElement("span", {
    className: "dash-lib-on"
  }, "On Dashboard") : addBtn(() => onAddReport(r, p)))))), reports && reports.length > 0 && /*#__PURE__*/React.createElement("p", {
    className: "dash-lib-note"
  }, "Only reports shared with you, or yours, are listed. A pinned chart follows the report: edits show up here.")));
}

/** Save as the workspace default (mockup D). People with their own layout keep it and get a one-time note. */
export function DefaultConfirm({
  customized,
  busy,
  onSave,
  onClose
}) {
  const others = Math.max(0, customized || 0);
  return /*#__PURE__*/React.createElement(Modal, {
    title: "Save as the workspace default?",
    onClose: onClose,
    width: 520,
    busy: busy,
    footer: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: btn + "k-button-solid-base",
      disabled: busy,
      onClick: onClose
    }, "Cancel"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: btn + "k-button-solid-primary",
      disabled: busy,
      onClick: onSave
    }, busy ? "Saving…" : "Save default"))
  }, /*#__PURE__*/React.createElement("p", null, "Everyone who hasn't customized their Dashboard will see this layout, and new people start from it."), /*#__PURE__*/React.createElement("p", null, others === 0 ? "Nobody has their own layout yet." : `${others} ${others === 1 ? "person has" : "people have"} their own layout. They keep it and see a note offering the new default.`));
}

/** Bottom toast. Closes after 12 s, held open while the pointer or focus is on it (time to reach Undo). */
export function Toast({
  text,
  action,
  onAction,
  onClose
}) {
  const [held, setHeld] = React.useState(false);
  React.useEffect(() => {
    if (held) return undefined;
    const t = setTimeout(onClose, 12000);
    return () => clearTimeout(t);
  }, [text, held]);
  return /*#__PURE__*/React.createElement("div", {
    className: "dash-toast",
    role: "status",
    onMouseEnter: () => setHeld(true),
    onMouseLeave: () => setHeld(false),
    onFocus: () => setHeld(true),
    onBlur: () => setHeld(false)
  }, /*#__PURE__*/React.createElement("span", null, text), action && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "nc-link strong",
    onClick: onAction
  }, action), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "dash-ic",
    "aria-label": "Dismiss",
    onClick: onClose
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons"
  }, "close")));
}